import { addToast } from "@caprikorn/dynamo-ui";
import {
  type FetchiosError,
  type FetchiosResponse,
  downloadData,
} from "@caprikorn/dynamo-utils";
import { useMutation } from "@tanstack/react-query";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { DOWNLOAD_TIMEOUT, apiEcmsHttp } from "@services/http";
import { QUERY_KEYS } from "@services/keys";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type { PostDocumentsDownloadParams } from "./apiEcms.types";

const postDocumentsDownload = ({
  customerId,
  contractId,
  extension,
  reference,
  family,
  last = true,
}: PostDocumentsDownloadParams) =>
  apiEcmsHttp.post<object, FetchiosResponse<Blob>>(
    "/documents/download",
    {
      customerId,
      contractId,
      family,
      last,
      extension,
      reference,
    },
    {
      timeout: DOWNLOAD_TIMEOUT,
      responseType: "blob",
    },
  );

export const usePostDocumentsDownload = () => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation(["main", "common"]);

  return useMutation<
    FetchiosResponse<Blob>,
    FetchiosError,
    PostDocumentsDownloadParams
  >({
    mutationFn: postDocumentsDownload,
    mutationKey: [QUERY_KEYS.BILL_DOWNLOAD, currentGroup],
    onSuccess: (response, args) => {
      if (response.status === 200) {
        downloadData(
          `${args.family}_${args.contractId}.${args.extension}`,
          response.data,
        );
      }
      if (response.status === 204) {
        addToast({
          variant: "error",
          title: t("perimeter.billsEmptyError.title"),
          description: t("perimeter.billsEmptyError.description"),
        });
      }
    },
    onError: (error) => {
      if (error.status === 413) {
        addToast({
          variant: "error",
          title: t("perimeter.billsTooLargeError.title"),
          description: t("perimeter.billsTooLargeError.description"),
        });

        return;
      }

      if (error.message === "DOCUMENT_NOT_FOUND") {
        addToast({
          variant: "error",
          title: t(
            "perimeter.sites.invoiceAndPaymentsDetails.formatUnavailableError",
          ),
        });

        return;
      }

      addToast({
        variant: "error",
        title: t("errors.download.title", { ns: "common" }),
        description: t("errors.download.description", { ns: "common" }),
      });
    },
  });
};
